import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import ModalPortal from '@Components/Popups/ModalPortal'
import Popup from '@Components/Popups/Popup'
import UserInfoAvatar from '@Components/UserInfoAvatar'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { StoresContext } from '@App/contexts'
import { getRightsholderUser } from '@App/api/routes/rightsholder/rightsholderUser'
import { PermissionGroups, UserGroup, UserInfo } from '@Entities/user/user'
import { useParams } from 'react-router'
import RootStore from '@App/stores/root-store'
import UserPermissionsModal from '@Scenes/RightsholderScene/RightsholderMaintenanceUserPermissionsProfile/UserPermissionsModal'
import { PermissionActions } from '@App/enum/permission-actions'
import PermissionHeadMenu from '@Scenes/RightsholderScene/RightsholderMaintenanceUserPermissionsProfile/PermissionHeadMenu'
import AssignedPermissionGroups from '@Scenes/RightsholderScene/RightsholderMaintenanceUserPermissionsProfile/AssignedPermissionGroups'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { PARAM } from '@App/navigation/param'

export interface MyUserGroup {
    name: string
    checked: boolean
    disabled?: boolean
}

const RightsholderMaintenanceUserPermissionsProfile = () => {
    const params = useParams<typeof PARAM>()
    if (!params.userId) {
        throw new Error('no user id')
    }
    const { userStore, rightsholderUserStore, popupStore, rightsholderStore } = useContext<RootStore>(StoresContext)

    const [rightsholderUser, setRightsholderUser] = useState<UserInfo>()
    const [actionType, setActionType] = useState<PermissionActions>()
    const [myUserGroups, setMyUserGroups] = useState<MyUserGroup[]>([])
    const [editActionsGroups, setEditActionsGroups] = useState<MyUserGroup[]>([])
    const [editIsDisabled, setEditIsDisabled] = useState<boolean>(true)
    const [confirmPopupIsOpen, setConfirmPopupIsOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [userId, setUserId] = useState<string>(params.userId)

    const setUsersGroup = (user?: UserInfo) => {
        const userGroupsCollection = userStore.permissionGroups
        const currentUserGroups = user && user?.groups
        if (currentUserGroups?.length) {
            const myUserGroupsMapped = userGroupsCollection.map((collectionItem: PermissionGroups) => {
                const result = currentUserGroups.find((userItem: UserGroup) => userItem.name === collectionItem.name)
                return {
                    ...collectionItem,
                    checked: !!result,
                }
            })
            setMyUserGroups(myUserGroupsMapped)
        } else {
            const myUserGroupsMapped = userGroupsCollection.map((collectionItem: PermissionGroups) => ({
                ...collectionItem,
                checked: false,
                disabled: false,
            }))
            setMyUserGroups(myUserGroupsMapped)
        }
    }

    const onCancelEdit = (user?: UserInfo) => {
        setUsersGroup(user)
        setEditActionsGroups([])
        setEditIsDisabled(true)
    }

    const onOpenConfirmPopup = (newActionType: PermissionActions) => {
        setActionType(newActionType)
        setConfirmPopupIsOpen(true)
    }

    const onCloseConfirmPopup = () => {
        setConfirmPopupIsOpen(false)
    }

    const getUsersForUser = async (user: UserInfo) => {
        const group = user?.groups?.map((item: UserGroup) => item.name).join()

        if (group) {
            setLoading(true)
            try {
                await rightsholderUserStore.loadRightsholderUsersForUser(userId, {
                    limit: 5,
                    group: group,
                })
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
            setLoading(false)
        }
    }

    const getUser = async () => {
        const currentRightsholderId = rightsholderStore.currentRightsholderId
        try {
            const user = await getRightsholderUser(currentRightsholderId, userId)
            setRightsholderUser(user.data)
            setUsersGroup(user.data)
            await getUsersForUser(user.data)
        } catch (error) {
            popupStore.setAlertFromError(error)
        }
    }

    useEffect(() => {
        analyticsSendPage('rightsholder_maintenance_user_permissions_profile')
        return () => {
            rightsholderUserStore.destroyRightsHolderUser()
        }
    }, [])

    useEffect(() => {
        const userIdFromParams = params.userId
        if (userId !== userIdFromParams) {
            if (!userIdFromParams) {
                throw new Error('no user id')
            }
            setUserId(userIdFromParams)
        }
    }, [params.userId])

    useEffect(() => {
        onCancelEdit(rightsholderUser)
        rightsholderUserStore.destroyRightsHolderUser()
        getUser().then()
    }, [userId])

    if (!rightsholderUser) {
        return <PointPreloader />
    }

    return (
        <>
            <title>User Permissions Profile</title>
            <div className="rightsholder-maintenance-user-permissions-profile-wrapper">
                <div className="global-container">
                    <PermissionHeadMenu onOpenConfirmPopup={onOpenConfirmPopup} />
                    <div className="head-title">
                        <h2>Profile</h2>
                        <UserInfoAvatar userInfo={rightsholderUser} />
                    </div>
                    {loading ? (
                        <PointPreloader />
                    ) : (
                        <AssignedPermissionGroups
                            setEditActionsGroups={setEditActionsGroups}
                            editIsDisabled={editIsDisabled}
                            setMyUserGroups={setMyUserGroups}
                            myUserGroups={myUserGroups}
                            rightsholderUser={rightsholderUser}
                            editActionsGroups={editActionsGroups}
                            onOpenConfirmPopup={onOpenConfirmPopup}
                            onCancelEdit={onCancelEdit}
                            setEditIsDisabled={setEditIsDisabled}
                        />
                    )}
                </div>

                <ModalPortal isOpen={confirmPopupIsOpen} onClose={() => setConfirmPopupIsOpen(false)}>
                    <Popup onClose={() => setConfirmPopupIsOpen(false)}>
                        <UserPermissionsModal
                            myUserGroups={myUserGroups}
                            setUsersGroup={setUsersGroup}
                            getUsersForUser={getUsersForUser}
                            rightsholderUser={rightsholderUser}
                            setRightsholderUser={setRightsholderUser}
                            onCloseConfirmPopup={onCloseConfirmPopup}
                            onCancelEdit={onCancelEdit}
                            editActionsGroups={editActionsGroups}
                            actionType={actionType}
                        />
                    </Popup>
                </ModalPortal>
            </div>
        </>
    )
}

export default observer(RightsholderMaintenanceUserPermissionsProfile)
